import React, { useEffect, useMemo, useCallback, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Col, Row, UncontrolledTooltip, Modal, ModalHeader, ModalBody, Form,
  Input, FormFeedback, Label, Card, CardBody, Button, Pagination,
  PaginationItem, PaginationLink, ModalFooter, Badge
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableContainer from '../../components/Common/TableContainer';
import Spinners from "components/Common/Spinner";
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';
import {
  fetchSoal,
  createSoal,
  updateSoal,
  deleteSoal,
} from '../../services/soalService';
import { fetchJenisSoal } from '../../services/Master/MasterJenisSoal';
import { fetchJenisSeleksi } from '../../services/Master/MasterJenisSeleksi';
import { fetchTipeKonten } from '../../services/Master/MasterTipeKonten';
import { fetchKategori } from '../../services/Master/kategoriService';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;
const DEFAULT_FILTERS = {
  jenis_soal_id: "",
  id_jenis_seleksi: "",
  id_tipe_konten: "",
  search: "",
  id_soal: "",
  id_kategori: "",
};

const initialState = {
  modal: false,
  isEdit: false,
  soal: null,
  data: [],
  loading: true,
  loadingOptions: false,
  loadingJenisSoalModal: false,
  jenisSoalOptions: [],
  jenisSoalModalOptions: [],
  jenisSeleksiOptions: [],
  tipeKontenOptions: [],
  filteredJenisSeleksi: [],
  filteredJenisSeleksiModal: [],
  kategoriOptions: [],
  isOptionsLoaded: false,
  deleteModal: false,
  pagination: {
    page: DEFAULT_PAGE,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
    last_page: 1
  },
  filters: { ...DEFAULT_FILTERS }
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, page: DEFAULT_PAGE }
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload }
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: { ...DEFAULT_FILTERS },
        pagination: { ...state.pagination, page: DEFAULT_PAGE }
      };
    case 'FILTER_JENIS_SELEKSI':
      return {
        ...state,
        filteredJenisSeleksi: action.payload
      };
    case 'FILTER_JENIS_SELEKSI_MODAL':
      return {
        ...state,
        filteredJenisSeleksiModal: action.payload
      };
    case 'SET_JENIS_SOAL_MODAL_OPTIONS':
      return {
        ...state,
        jenisSoalModalOptions: action.payload
      };
    default:
      return state;
  }
}

const SoalList = () => {
  document.title = "Daftar Soal";
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idSoal = queryParams.get('id_soal');

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    filters: { ...initialState.filters, id_soal: idSoal || "" }
  });

  const {
    modal, isEdit, soal, data, loading,
    jenisSoalOptions, tipeKontenOptions, kategoriOptions,
    filteredJenisSeleksi, filteredJenisSeleksiModal,
    jenisSoalModalOptions, loadingJenisSoalModal,
    isOptionsLoaded, deleteModal, pagination, filters, loadingOptions
  } = state;

  // Fungsi untuk mengecek apakah soal adalah TKP
  const isTKP = useCallback((jenisSoalId) => {
    if (!jenisSoalId) return false;
    const jenisSoal = jenisSoalOptions.find(js => js.id_jenis_soal === parseInt(jenisSoalId));
    return jenisSoal?.nama_jenis?.toLowerCase().includes('tkp') || false;
  }, [jenisSoalOptions]);

  // Formik validation schema dengan penambahan validasi untuk TKP
  const validationSchema = useMemo(() => Yup.object({
    id_tipe_konten: Yup.number().required("Tipe Konten wajib diisi"),
    id_jenis_soal: Yup.number().required("Jenis Soal wajib diisi"),
    id_jenis_seleksi: Yup.number().required("Jenis Seleksi wajib diisi"),
    soal: Yup.string().required("Soal wajib diisi"),
    opsi_a: Yup.string().required("Opsi A wajib diisi"),
    opsi_b: Yup.string().required("Opsi B wajib diisi"),
    opsi_c: Yup.string().required("Opsi C wajib diisi"),
    opsi_d: Yup.string().required("Opsi D wajib diisi"),
    opsi_e: Yup.string().nullable(),
    jawaban_benar: Yup.string()
      .required("Jawaban benar wajib diisi")
      .oneOf(['A', 'B', 'C', 'D', 'E'], 'Jawaban harus A, B, C, D, atau E'),
    pembahasan: Yup.string().nullable(),
    // Bobot hanya wajib untuk non-TKP
    bobot: Yup.number()
      .when('id_jenis_soal', {
        is: (id_jenis_soal) => !isTKP(id_jenis_soal),
        then: (schema) => schema.required("Bobot wajib diisi").min(1, "Bobot minimal 1"),
        otherwise: (schema) => schema.nullable()
      }),
    // Validasi untuk nilai TKP (wajib jika TKP)
    nilai_opsi_a: Yup.number()
      .when('id_jenis_soal', {
        is: (id_jenis_soal) => isTKP(id_jenis_soal),
        then: (schema) => schema.required("Nilai Opsi A wajib diisi untuk TKP").typeError("Nilai harus angka"),
        otherwise: (schema) => schema.nullable().typeError("Nilai harus angka")
      }),
    nilai_opsi_b: Yup.number()
      .when('id_jenis_soal', {
        is: (id_jenis_soal) => isTKP(id_jenis_soal),
        then: (schema) => schema.required("Nilai Opsi B wajib diisi untuk TKP").typeError("Nilai harus angka"),
        otherwise: (schema) => schema.nullable().typeError("Nilai harus angka")
      }),
    nilai_opsi_c: Yup.number()
      .when('id_jenis_soal', {
        is: (id_jenis_soal) => isTKP(id_jenis_soal),
        then: (schema) => schema.required("Nilai Opsi C wajib diisi untuk TKP").typeError("Nilai harus angka"),
        otherwise: (schema) => schema.nullable().typeError("Nilai harus angka")
      }),
    nilai_opsi_d: Yup.number()
      .when('id_jenis_soal', {
        is: (id_jenis_soal) => isTKP(id_jenis_soal),
        then: (schema) => schema.required("Nilai Opsi D wajib diisi untuk TKP").typeError("Nilai harus angka"),
        otherwise: (schema) => schema.nullable().typeError("Nilai harus angka")
      }),
    nilai_opsi_e: Yup.number()
      .when('id_jenis_soal', {
        is: (id_jenis_soal) => isTKP(id_jenis_soal),
        then: (schema) => schema.nullable().typeError("Nilai harus angka"),
        otherwise: (schema) => schema.nullable().typeError("Nilai harus angka")
      }),
  }), [isTKP]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id_tipe_konten: soal?.id_tipe_konten || "",
      id_jenis_soal: soal?.id_jenis_soal || "",
      id_jenis_seleksi: soal?.id_jenis_seleksi || "",
      id_kategori: soal?.id_kategori || "",
      soal: soal?.soal || "",
      image_soal: null,
      opsi_a: soal?.opsi_a || "",
      opsi_b: soal?.opsi_b || "",
      opsi_c: soal?.opsi_c || "",
      opsi_d: soal?.opsi_d || "",
      opsi_e: soal?.opsi_e || "",
        image_soal: undefined, // UBAH DARI null JADI undefined
    image_opsi_b: undefined, // UBAH DARI null JADI undefined
    image_opsi_c: undefined, // UBAH DARI null JADI undefined
    image_opsi_d: undefined, // UBAH DARI null JADI undefined
    image_opsi_e: undefined, // UBAH DARI null JADI undefined
      jawaban_benar: soal?.jawaban_benar || "",
      pembahasan: soal?.pembahasan || "",
      image_pembahasan: undefined, 
      bobot: soal?.bobot || "",
      // Nilai TKP
      nilai_opsi_a: soal?.nilai_opsi_a || "",
      nilai_opsi_b: soal?.nilai_opsi_b || "",
      nilai_opsi_c: soal?.nilai_opsi_c || "",
      nilai_opsi_d: soal?.nilai_opsi_d || "",
      nilai_opsi_e: soal?.nilai_opsi_e || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        const formData = new FormData();

        const isCurrentSoalTKP = isTKP(values.id_jenis_soal);

        Object.keys(values).forEach(key => {
          if (key.startsWith('image_')) {
            const file = values[key];
            if (file instanceof File) {
              formData.append(key, file);
            } else if (isEdit && file === null && soal?.[key]) {
              formData.append(key, '');
            }
          } else if (['id_jenis_soal', 'id_jenis_seleksi', 'bobot', 'id_tipe_konten', 'id_kategori', 'nilai_opsi_a', 'nilai_opsi_b', 'nilai_opsi_c', 'nilai_opsi_d', 'nilai_opsi_e'].includes(key)) {
            // Handle nilai TKP - hanya kirim jika TKP
            if (key.startsWith('nilai_opsi_')) {
              if (isCurrentSoalTKP && values[key] !== null && values[key] !== "") {
                formData.append(key, Number(values[key]));
              } else {
                formData.append(key, '');
              }
            } else if (key === 'bobot' && isCurrentSoalTKP) {
              // Untuk TKP, bobot tidak dikirim atau dikirim null
              formData.append(key, '');
            } else {
              formData.append(key, values[key] !== null && values[key] !== "" ? Number(values[key]) : '');
            }
          } else {
            formData.append(key, values[key] || '');
          }
        });

        const response = isEdit
          ? await updateSoal(soal.id_soal, formData)
          : await createSoal(formData);

        if (response.success) {
          toast.success(response.message || (isEdit ? "Soal berhasil diperbarui" : "Soal berhasil ditambahkan"));
          fetchData();
          toggleModal();
          if (idSoal && !isEdit) {
            navigate(location.pathname, { replace: true });
          }
        } else {
          throw new Error(response.message || "Gagal menyimpan soal.");
        }
      } catch (error) {
        console.error("Form submission error:", error);
        toast.error(error.message || "Terjadi kesalahan saat menyimpan.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Cek apakah soal yang sedang diedit/ditambah adalah TKP
  const isCurrentSoalTKP = useMemo(() => {
    return isTKP(formik.values.id_jenis_soal);
  }, [formik.values.id_jenis_soal, isTKP]);

  // Reset nilai TKP ketika jenis soal berubah dari TKP ke non-TKP atau sebaliknya
  useEffect(() => {
    if (!isCurrentSoalTKP) {
      // Reset nilai TKP jika bukan TKP
      formik.setFieldValue("nilai_opsi_a", "");
      formik.setFieldValue("nilai_opsi_b", "");
      formik.setFieldValue("nilai_opsi_c", "");
      formik.setFieldValue("nilai_opsi_d", "");
      formik.setFieldValue("nilai_opsi_e", "");
    } else {
      // Set default values untuk TKP jika diperlukan
      if (!formik.values.nilai_opsi_a && formik.values.nilai_opsi_a !== 0) formik.setFieldValue("nilai_opsi_a", 0);
      if (!formik.values.nilai_opsi_b && formik.values.nilai_opsi_b !== 0) formik.setFieldValue("nilai_opsi_b", 0);
      if (!formik.values.nilai_opsi_c && formik.values.nilai_opsi_c !== 0) formik.setFieldValue("nilai_opsi_c", 0);
      if (!formik.values.nilai_opsi_d && formik.values.nilai_opsi_d !== 0) formik.setFieldValue("nilai_opsi_d", 0);
      if (!formik.values.nilai_opsi_e && formik.values.nilai_opsi_e !== 0) formik.setFieldValue("nilai_opsi_e", 0);
    }
  }, [isCurrentSoalTKP, formik.setFieldValue]);

const filterJenisSeleksi = useCallback(async (idTipeKonten) => {
  try {
    if (!idTipeKonten) {
      dispatch({ type: 'FILTER_JENIS_SELEKSI', payload: [] });
      return;
    }

    // ✅ BENAR - juga pakai filters
    const response = await fetchJenisSeleksi({
      filters: {
        id_tipe_konten: idTipeKonten
      },
      disable_pagination: true
    });

    const filtered = response.data || [];
    dispatch({ type: 'FILTER_JENIS_SELEKSI', payload: filtered });

  } catch (error) {
    console.error("Error filtering jenis seleksi:", error);
  }
}, [filters.id_jenis_seleksi]);

const filterJenisSeleksiForModal = useCallback(async (idTipeKonten) => {
  try {
    if (!idTipeKonten) {
      dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: [] });
      return;
    }

    // ✅ BENAR - kirim id_tipe_konten dalam filters
    const response = await fetchJenisSeleksi({
      filters: {
        id_tipe_konten: idTipeKonten  // INI YANG BENAR!
      },
      disable_pagination: true
    });

    console.log('🔍 Response jenis seleksi:', response.data);
    dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: response.data || [] });

  } catch (error) {
    console.error("Error filtering jenis seleksi:", error);
    toast.error("Gagal memuat jenis seleksi");
  }
}, []);

  const fetchJenisSoalForModal = useCallback(async (idTipeKonten, idJenisSeleksi) => {
    if (!idTipeKonten || !idJenisSeleksi) {
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
      return;
    }

    dispatch({ type: 'SET_STATE', payload: { loadingJenisSoalModal: true } });
    try {
      const response = await fetchJenisSoal({
        id_tipe_konten: idTipeKonten,
        id_jenis_seleksi: idJenisSeleksi,
        disable_pagination: true
      });

      if (response.success && response.data) {
        dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: response.data });
      } else {
        dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
        console.error("Failed to fetch jenis soal for modal:", response.message);
      }
    } catch (error) {
      console.error("Error fetching jenis soal for modal:", error);
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
    } finally {
      dispatch({ type: 'SET_STATE', payload: { loadingJenisSoalModal: false } });
    }
  }, []);

  useEffect(() => {
    if (isOptionsLoaded && filters.id_tipe_konten) {
      filterJenisSeleksi(filters.id_tipe_konten);
    }
  }, [filters.id_tipe_konten, isOptionsLoaded, filterJenisSeleksi]);

  const fetchData = useCallback(async () => {
    if (!isOptionsLoaded) return;

    try {
      dispatch({ type: 'SET_STATE', payload: { loading: true } });

      const response = await fetchSoal(
        pagination.page,
        pagination.per_page,
        filters
      );

      if (response.success) {
        const calculatedLastPage = Math.ceil(response.meta.total / response.meta.per_page);
        const soalData = response.data.map(soalItem => ({
          ...soalItem,
          jenis_seleksi_nama: soalItem.jenis_seleksi_nama,
          jenis_soal_nama: soalItem.jenis_soal_nama
        }));

        dispatch({
          type: 'SET_STATE',
          payload: {
            data: soalData,
            pagination: {
              ...pagination,
              total: response.meta.total,
              last_page: calculatedLastPage
            },
            loading: false
          }
        });
      } else {
        throw new Error(response.message || "Gagal memuat data soal.");
      }
    } catch (error) {
      console.error("Error in fetchData:", error);
      dispatch({ type: 'SET_STATE', payload: { loading: false } });
      toast.error(error.message || "Gagal memuat data soal.");
    }
  }, [isOptionsLoaded, pagination.page, pagination.per_page, filters]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        dispatch({ type: 'SET_STATE', payload: { loadingOptions: true } });

        const [jenisSoalRes, tipeKontenRes, kategoriRes] = await Promise.all([
          fetchJenisSoal(1, 100, {}, true),
          fetchTipeKonten(1, 100, {}, true),
          fetchKategori(1, 100, {}, true)
        ]);

        dispatch({
          type: 'SET_STATE',
          payload: {
            jenisSoalOptions: jenisSoalRes.data || [],
            tipeKontenOptions: tipeKontenRes.data || [],
            kategoriOptions: kategoriRes.data || [],
            isOptionsLoaded: true,
            loadingOptions: false
          }
        });
      } catch (error) {
        console.error("Error loading options:", error);
        toast.error("Gagal memuat opsi pilihan. Silakan coba lagi.");
        dispatch({ type: 'SET_STATE', payload: { loadingOptions: false } });
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    if (isOptionsLoaded) {
      fetchData();
    }
  }, [isOptionsLoaded, fetchData]);

  useEffect(() => {
    if (idSoal && isOptionsLoaded) {
      const fetchSingleSoal = async (id) => {
        try {
          dispatch({ type: 'SET_STATE', payload: { loading: true } });
          const response = await fetchSoal(1, 1, { id_soal: id });

          if (response.success && response.data.length > 0) {
            const fetchedSoal = response.data[0];
            dispatch({
              type: 'SET_STATE',
              payload: {
                soal: fetchedSoal,
                isEdit: true,
                modal: true,
                loading: false
              }
            });
            formik.setFieldValue("id_tipe_konten", fetchedSoal.id_tipe_konten || "");
            formik.setFieldValue("id_jenis_seleksi", fetchedSoal.id_jenis_seleksi || "");
            formik.setFieldValue("id_jenis_soal", fetchedSoal.id_jenis_soal || "");
          } else {
            throw new Error("Soal tidak ditemukan.");
          }
        } catch (error) {
          console.error("Error fetching single soal:", error);
          toast.error(error.message || "Gagal memuat data soal.");
          dispatch({ type: 'SET_STATE', payload: { loading: false } });
          navigate(-1);
        }
      };

      fetchSingleSoal(idSoal);
    }
  }, [idSoal, isOptionsLoaded, navigate]);

  useEffect(() => {
    if (modal && formik.values.id_tipe_konten) {
      filterJenisSeleksiForModal(formik.values.id_tipe_konten);
    } else if (modal && !formik.values.id_tipe_konten) {
      dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: [] });
    }
  }, [modal, formik.values.id_tipe_konten, filterJenisSeleksiForModal]);

  useEffect(() => {
    if (modal && formik.values.id_tipe_konten && formik.values.id_jenis_seleksi) {
      fetchJenisSoalForModal(formik.values.id_tipe_konten, formik.values.id_jenis_seleksi);
    } else if (modal) {
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
    }
  }, [modal, formik.values.id_tipe_konten, formik.values.id_jenis_seleksi, fetchJenisSoalForModal]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage < 1 || newPage > pagination.last_page) return;

    dispatch({
      type: 'SET_PAGINATION',
      payload: { page: newPage }
    });
  }, [pagination.last_page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'id_tipe_konten') {
      dispatch({
        type: 'SET_FILTERS',
        payload: {
          [name]: value,
          id_jenis_seleksi: ""
        }
      });
    } else {
      dispatch({
        type: 'SET_FILTERS',
        payload: { [name]: value }
      });
    }
  };

  const clearFilters = () => {
    dispatch({ type: 'RESET_FILTERS' });
  };

  const toggleModal = () => {
    if (modal) {
      dispatch({
        type: 'SET_STATE',
        payload: {
          modal: false,
          soal: null,
          isEdit: false,
          jenisSoalModalOptions: [],
          filteredJenisSeleksiModal: [],
        }
      });
      formik.resetForm();
      if (idSoal) {
        navigate(location.pathname, { replace: true });
      }
    } else {
      dispatch({ type: 'SET_STATE', payload: { modal: true, isEdit: false, soal: null } });
      formik.resetForm();
    }
  };

  const handleSoalClick = useCallback((soalData) => {
    dispatch({
      type: 'SET_STATE',
      payload: {
        soal: soalData,
        isEdit: true,
        modal: true
      }
    });
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'SET_STATE', payload: { deleteModal: false, soal: null } });
  }, []);

  const handleConfirmDelete = async () => {
  try {
    if (!soal?.id_soal) return;

    const response = await deleteSoal(soal.id_soal);
    if (response.success) {
      toast.success("Soal berhasil dihapus.");
      const newPage = data.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
      dispatch({
        type: 'SET_PAGINATION',
        payload: { page: newPage }
      });
      handleCloseDeleteModal();
      fetchData(); // ⬅️ TAMBAH INI
    } else {
      throw new Error(response.message || "Gagal menghapus soal.");
    }
  } catch (error) {
    console.error("Error deleting soal:", error);
    toast.error(error.message || "Gagal menghapus soal.");
  }
};
  const onClickDelete = useCallback((soalData) => {
    dispatch({
      type: 'SET_STATE',
      payload: {
        soal: soalData,
        deleteModal: true
      }
    });
  }, []);

const renderImagePreview = useCallback((imageFieldName) => {
  const fileInput = formik.values[imageFieldName];
  const existingImageUrl = soal?.[imageFieldName];

  console.log(`Debug ${imageFieldName}:`, {
    fileInput,
    existingImageUrl,
    isEdit,
    formikValue: formik.values[imageFieldName]
  });

  // Jika upload file baru
  if (fileInput instanceof File) {
    return (
      <div className="mt-2">
        <img
          src={URL.createObjectURL(fileInput)}
          alt={`Preview ${imageFieldName.replace('image_', '')}`}
          className="img-thumbnail"
          style={{ maxWidth: '100px', maxHeight: '100px' }}
        />
        <small className="d-block text-muted">Preview Gambar Baru</small>
        <Button
          size="sm"
          color="danger"
          outline
          className="ms-2 mt-1"
          onClick={() => {
            formik.setFieldValue(imageFieldName, null);
            const fileInputElement = document.getElementById(imageFieldName);
            if (fileInputElement) {
              fileInputElement.value = '';
            }
          }}
        >
          Hapus Gambar
        </Button>
      </div>
    );
  } 
  // Jika ada gambar yang sudah ada di database DAN belum ditandai untuk dihapus
  else if (isEdit && existingImageUrl && formik.values[imageFieldName] !== null) {
    return (
      <div className="mt-2">
        <img
          src={existingImageUrl}
          alt={`Gambar ${imageFieldName.replace('image_', '')} Saat Ini`}
          className="img-thumbnail"
          style={{ maxWidth: '100px', maxHeight: '100px' }}
        />
        <small className="d-block text-muted">Gambar Saat Ini</small>
        <Button
          size="sm"
          color="danger"
          outline
          className="ms-2 mt-1"
          onClick={() => {
            console.log(`Menghapus ${imageFieldName}`);
            formik.setFieldValue(imageFieldName, null);
          }}
        >
          Hapus Gambar
        </Button>
      </div>
    );
  }
  // Jika gambar ditandai untuk dihapus (null)
  else if (isEdit && formik.values[imageFieldName] === null && existingImageUrl) {
    return (
      <div className="mt-2">
        <div className="alert alert-warning py-2">
          <small>
            <i className="mdi mdi-alert-circle-outline me-1"></i>
            Gambar akan dihapus saat disimpan
          </small>
        </div>
        <Button
          size="sm"
          color="success"
          outline
          className="ms-2 mt-1"
          onClick={() => {
            console.log(`Membatalkan hapus ${imageFieldName}`);
            formik.setFieldValue(imageFieldName, undefined);
          }}
        >
          Batalkan Hapus
        </Button>
      </div>
    );
  }
  return null;
}, [formik.values, isEdit, soal, formik.setFieldValue]);
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link'
  ];

  const columns = useMemo(() => [
    {
      header: 'No',
      accessorKey: "id_soal",
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="text-muted">
          {((pagination.page - 1) * pagination.per_page) + cell.row.index + 1}
        </span>
      )
    },
    {
      header: 'Jenis Seleksi',
      accessorKey: "jenis_seleksi_nama",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="primary" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Jenis Soal',
      accessorKey: "jenis_soal_nama",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="info" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Kategori',
      accessorKey: "kategori",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="secondary" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Soal',
      accessorKey: "soal",
      enableColumnFilter: false,
      cell: (cell) => (
        <div className="text-truncate" style={{ maxWidth: '200px' }}>
          {cell.getValue() || '-'}
        </div>
      )
    },
    {
      header: 'Jawaban',
      accessorKey: "jawaban_benar",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge
          color={cell.getValue() === 'A' ? 'success' :
            cell.getValue() === 'B' ? 'primary' :
              cell.getValue() === 'C' ? 'warning' :
                cell.getValue() === 'D' ? 'danger' : 'secondary'}
          className="font-size-12"
        >
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Bobot',
      accessorKey: "bobot",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="dark" className="font-size-12">
          {cell.getValue() || '0'}
        </Badge>
      )
    },
    {
      header: 'Dibuat',
      accessorKey: "created_at",
      enableColumnFilter: false,
      cell: (cellProps) => (
        new Date(cellProps.getValue()).toLocaleDateString('id-ID', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      )
    },
    {
      header: 'Aksi',
      accessorKey: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (cellProps) => {
        if (!cellProps.row.original) return null;

        return (
          <div className="hstack gap-2">
            <Button
              color="soft-info"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleSoalClick(cellProps.row.original);
              }}
              id={`edittooltip-${cellProps.row.original.id_soal}`}
            >
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`edittooltip-${cellProps.row.original.id_soal}`}
            >
              Edit
            </UncontrolledTooltip>

            <Button
              color="soft-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClickDelete(cellProps.row.original);
              }}
              id={`deletetooltip-${cellProps.row.original.id_soal}`}
            >
              <i className="mdi mdi-delete-outline" />
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`deletetooltip-${cellProps.row.original.id_soal}`}
            >
              Hapus
            </UncontrolledTooltip>
          </div>
        );
      }
    }
  ], [pagination.page, pagination.per_page, handleSoalClick, onClickDelete]);

  return (
    <React.Fragment>
      <ToastContainer position="top-right" autoClose={3000} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Soal" breadcrumbItem="List Soal" />

          {loading && !isOptionsLoaded ? (
            <Spinners />
          ) : (
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0 card-title">Daftar Soal</h5>
                      </div>
                      <Button color="primary" onClick={toggleModal}>
                        <i className="mdi mdi-plus me-1"></i> Tambah Soal
                      </Button>
                    </div>
                  </CardBody>

                  <CardBody>
                    <div className="mb-4">
                      <Row className="g-3">
                        <Col md={3}>
                          <Label htmlFor="id_tipe_konten_filter">Tipe Konten</Label>
                          <Input
                            type="select"
                            name="id_tipe_konten"
                            id="id_tipe_konten_filter"
                            value={filters.id_tipe_konten}
                            onChange={handleFilterChange}
                            className="form-select"
                          >
                            <option value="">Semua Tipe Konten</option>
                            {tipeKontenOptions.map((tipe) => (
                              <option key={tipe.id_tipe_konten} value={tipe.id_tipe_konten}>
                                {tipe.tipe_konten}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={3}>
                          <Label htmlFor="id_jenis_seleksi_filter">Jenis Seleksi</Label>
                          <Input
                            type="select"
                            name="id_jenis_seleksi"
                            id="id_jenis_seleksi_filter"
                            value={filters.id_jenis_seleksi}
                            onChange={handleFilterChange}
                            className="form-select"
                            disabled={!filters.id_tipe_konten}
                          >
                            <option value="">Semua Seleksi</option>
                            {filteredJenisSeleksi.map((seleksi) => (
                              <option key={seleksi.id_jenis_seleksi} value={seleksi.id_jenis_seleksi}>
                                {seleksi.nama_seleksi}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={3}>
                          <Label htmlFor="jenis_soal_filter">Jenis Soal</Label>
                          <Input
                            type="select"
                            name="jenis_soal_id"
                            id="jenis_soal_filter"
                            value={filters.jenis_soal_id}
                            onChange={handleFilterChange}
                            className="form-select"
                          >
                            <option value="">Semua Jenis Soal</option>
                            {jenisSoalOptions.map((jenis) => (
                              <option key={jenis.id_jenis_soal} value={jenis.id_jenis_soal}>
                                {jenis.nama_jenis}
                              </option>
                            ))}
                          </Input>
                        </Col>

                    
                        <Col md={3} className="d-flex align-items-end">
                          <Button
                            color="danger"
                            outline
                            onClick={clearFilters}
                            className="w-100"
                          >
                            <i className="mdi mdi-refresh me-1"></i> Reset
                          </Button>
                        </Col>
                      </Row>
                    </div>

                    <TableContainer
                      columns={columns}
                      data={data}
                      isGlobalFilter={false}
                      isPagination={false}
                      customPageSize={pagination.per_page}
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    />

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted">
                        Menampilkan <span className="fw-semibold">{pagination.total > 0 ? ((pagination.page - 1) * pagination.per_page) + 1 : 0}</span> sampai{' '}
                        <span className="fw-semibold">{Math.min(pagination.page * pagination.per_page, pagination.total)}</span> dari{' '}
                        <span className="fw-semibold">{pagination.total}</span> data
                      </div>

                      <div className="d-flex align-items-center">
                        <Pagination className="pagination pagination-rounded mb-0">
                          <PaginationItem disabled={pagination.page === 1}>
                            <PaginationLink previous onClick={() => handlePageChange(pagination.page - 1)} />
                          </PaginationItem>

                          {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                            let pageNum;
                            if (pagination.last_page <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.last_page - 2) {
                              pageNum = pagination.last_page - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }

                            return (
                              <PaginationItem key={pageNum} active={pageNum === pagination.page}>
                                <PaginationLink onClick={() => handlePageChange(pageNum)}>
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          })}

                          <PaginationItem disabled={pagination.page === pagination.last_page}>
                            <PaginationLink next onClick={() => handlePageChange(pagination.page + 1)} />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          <Modal isOpen={modal} toggle={toggleModal} size="xl" centered>
            <ModalHeader toggle={toggleModal} tag="h4" className="bg-light p-3">
              {isEdit ? "Edit Soal" : "Tambah Soal"}
            </ModalHeader>

            <ModalBody className="px-4 pt-4">
              <Form onSubmit={formik.handleSubmit}>
                <Row className="g-4">
                  <Col md={6}>
                    <Card className="shadow-sm border h-100">
                      <CardBody>
                        <h5 className="font-size-16 mb-3 border-bottom pb-2">Soal & Informasi Dasar</h5>

                        <div className="mb-3">
                          <Label htmlFor="id_tipe_konten" className="form-label">Tipe Konten <span className="text-danger">*</span></Label>
                          <Input
                            id="id_tipe_konten"
                            name="id_tipe_konten"
                            type="select"
                            onChange={async (e) => {
                              const idTipeKonten = e.target.value;
                              formik.setFieldValue("id_tipe_konten", idTipeKonten);
                              formik.setFieldValue("id_jenis_seleksi", "");
                              formik.setFieldValue("id_jenis_soal", "");
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_tipe_konten || ""}
                            invalid={!!(formik.touched.id_tipe_konten && formik.errors.id_tipe_konten)}
                            className="form-select"
                          >
                            <option value="">Pilih Tipe Konten</option>
                            {tipeKontenOptions.map((tipe) => (
                              <option key={tipe.id_tipe_konten} value={tipe.id_tipe_konten}>
                                {tipe.tipe_konten}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_tipe_konten}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="id_jenis_seleksi" className="form-label">Jenis Seleksi <span className="text-danger">*</span></Label>
                          <Input
                            id="id_jenis_seleksi"
                            name="id_jenis_seleksi"
                            type="select"
                            onChange={async (e) => {
                              const selectedJenisSeleksiId = e.target.value;
                              formik.setFieldValue("id_jenis_seleksi", selectedJenisSeleksiId);
                              formik.setFieldValue("id_jenis_soal", "");
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_jenis_seleksi || ""}
                            invalid={!!(formik.touched.id_jenis_seleksi && formik.errors.id_jenis_seleksi)}
                            className="form-select"
                            disabled={!formik.values.id_tipe_konten || filteredJenisSeleksiModal.length === 0}
                          >
                            <option value="">
                              {!formik.values.id_tipe_konten ? "Pilih Tipe Konten dulu" :
                                (filteredJenisSeleksiModal.length === 0 && formik.values.id_tipe_konten) ? "Tidak ada Jenis Seleksi" : "Pilih Jenis Seleksi"
                              }
                            </option>
                            {filteredJenisSeleksiModal.map((seleksi) => (
                              <option key={seleksi.id_jenis_seleksi} value={seleksi.id_jenis_seleksi}>
                                {seleksi.nama_seleksi}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_jenis_seleksi}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="id_jenis_soal" className="form-label">Jenis Soal <span className="text-danger">*</span></Label>
                          <Input
                            id="id_jenis_soal"
                            name="id_jenis_soal"
                            type="select"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_jenis_soal || ""}
                            invalid={!!(formik.touched.id_jenis_soal && formik.errors.id_jenis_soal)}
                            className="form-select"
                            disabled={
                              !formik.values.id_tipe_konten ||
                              !formik.values.id_jenis_seleksi ||
                              loadingJenisSoalModal ||
                              jenisSoalModalOptions.length === 0
                            }
                          >
                            <option value="">
                              {loadingJenisSoalModal ? "Memuat Jenis Soal..." :
                                (!formik.values.id_tipe_konten || !formik.values.id_jenis_seleksi) ? "Pilih Tipe Konten & Jenis Seleksi" :
                                  (jenisSoalModalOptions.length === 0) ? "Tidak ada Jenis Soal" :
                                    "Pilih Jenis Soal"}
                            </option>
                            {jenisSoalModalOptions.map((jenis) => (
                              <option key={jenis.id_jenis_soal} value={jenis.id_jenis_soal}>
                                {jenis.nama_jenis}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_jenis_soal}</FormFeedback>
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="id_kategori" className="form-label">Kategori</Label>
                          <Input
                            id="id_kategori"
                            name="id_kategori"
                            type="select"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_kategori || ""}
                            invalid={!!(formik.touched.id_kategori && formik.errors.id_kategori)}
                            className="form-select"
                          >
                            <option value="">Pilih Kategori</option>
                            {kategoriOptions.map((kategori) => (
                              <option key={kategori.id_kategori} value={kategori.id_kategori_paket}>
                                {kategori.kategori}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_kategori}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="soal" className="form-label">Teks Soal <span className="text-danger">*</span></Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.soal || ''}
                            onChange={(content) => {
                              formik.setFieldValue("soal", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("soal", true);
                            }}
                            modules={modules}
                            formats={formats}
                            bounds={'.app'}
                            placeholder="Masukkan teks soal..."
                            style={{ height: '200px', marginBottom: '80px' }}
                          />
                          {formik.touched.soal && formik.errors.soal ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.soal}</div>
                          ) : null}
                        </div>

                        <div className="mb-0">
                          <Label htmlFor="image_soal" className="form-label">Gambar Soal (Opsional)</Label>
                          <Input
                            id="image_soal"
                            name="image_soal"
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                              formik.setFieldValue("image_soal", event.currentTarget.files[0] || null);
                            }}
                            className="form-control"
                          />
                          {renderImagePreview("image_soal")}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="shadow-sm border h-100">
                      <CardBody>
                        <div className="mb-3">
                          <Label htmlFor="jawaban_benar" className="form-label">Jawaban Benar <span className="text-danger">*</span></Label>
                          <Input
                            id="jawaban_benar"
                            name="jawaban_benar"
                            type="select"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.jawaban_benar || ""}
                            invalid={!!(formik.touched.jawaban_benar && formik.errors.jawaban_benar)}
                            className="form-select"
                          >
                            <option value="">Pilih Jawaban</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                            <option value="D">D</option>
                            <option value="E">E</option>
                          </Input>
                          <FormFeedback>{formik.errors.jawaban_benar}</FormFeedback>
                        </div>

                        {/* Sembunyikan bobot untuk TKP */}
                        {!isCurrentSoalTKP && (
                          <div className="mb-3">
                            <Label htmlFor="bobot" className="form-label">Bobot <span className="text-danger">*</span></Label>
                            <Input
                              id="bobot"
                              name="bobot"
                              type="number"
                              placeholder="Masukkan bobot soal"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.bobot || ""}
                              invalid={!!(formik.touched.bobot && formik.errors.bobot)}
                            />
                            <FormFeedback>{formik.errors.bobot}</FormFeedback>
                          </div>
                        )}

                        <div className="mb-3">
                          <Label htmlFor="pembahasan" className="form-label">Pembahasan (Opsional)</Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.pembahasan || ''}
                            onChange={(content) => {
                              formik.setFieldValue("pembahasan", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("pembahasan", true);
                            }}
                            modules={modules}
                            formats={formats}
                            placeholder="Masukkan pembahasan soal..."
                            style={{ height: '100px', marginBottom: '80px' }}
                          />
                          {formik.touched.pembahasan && formik.errors.pembahasan ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.pembahasan}</div>
                          ) : null}
                        </div>

                        <div className="mb-0">
                          <Label htmlFor="image_pembahasan" className="form-label">Gambar Pembahasan (Opsional)</Label>
                          <Input
                            id="image_pembahasan"
                            name="image_pembahasan"
                            type="file"
                            accept="image/*"
                            onChange={(event) => formik.setFieldValue("image_pembahasan", event.currentTarget.files[0] || null)}
                            className="form-control"
                          />
                          {renderImagePreview("image_pembahasan")}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* OPSI A - C */}
                  <Col md={6}>
                    <Card className="shadow-sm border h-100">
                      <CardBody>
                        <h5 className="font-size-16 mb-3 border-bottom pb-2">Opsi Jawaban</h5>
                        
                        {/* Opsi A */}
                        <div className="mb-3 border-bottom pb-3">
                          <Label htmlFor="opsi_a" className="form-label">Opsi A <span className="text-danger">*</span></Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.opsi_a || ''}
                            onChange={(content) => {
                              formik.setFieldValue("opsi_a", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("opsi_a", true);
                            }}
                            modules={modules}
                            formats={formats}
                            placeholder="Masukkan teks opsi A"
                            style={{ height: '100px', marginBottom: '80px' }}
                          />
                          {formik.touched.opsi_a && formik.errors.opsi_a ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.opsi_a}</div>
                          ) : null}
                          
                          {/* Input Nilai TKP untuk Opsi A - hanya tampil untuk TKP */}
                          {isCurrentSoalTKP && (
                            <div className="mt-2">
                              <Label htmlFor="nilai_opsi_a" className="form-label">Nilai TKP Opsi A <span className="text-danger">*</span></Label>
                              <Input
                                id="nilai_opsi_a"
                                name="nilai_opsi_a"
                                type="number"
                                placeholder="Masukkan nilai untuk opsi A"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.nilai_opsi_a || ""}
                                invalid={!!(formik.touched.nilai_opsi_a && formik.errors.nilai_opsi_a)}
                              />
                              <FormFeedback>{formik.errors.nilai_opsi_a}</FormFeedback>
                            </div>
                          )}
                          
                          <Input
                            id="image_opsi_a"
                            name="image_opsi_a"
                            type="file"
                            accept="image/*"
                            onChange={(event) => formik.setFieldValue("image_opsi_a", event.currentTarget.files[0] || null)}
                            className="form-control mt-2"
                          />
                          {renderImagePreview("image_opsi_a")}
                        </div>

                        {/* Opsi B */}
                        <div className="mb-3 border-bottom pb-3">
                          <Label htmlFor="opsi_b" className="form-label">Opsi B <span className="text-danger">*</span></Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.opsi_b || ''}
                            onChange={(content) => {
                              formik.setFieldValue("opsi_b", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("opsi_b", true);
                            }}
                            modules={modules}
                            formats={formats}
                            placeholder="Masukkan teks opsi B"
                            style={{ height: '100px', marginBottom: '80px' }}
                          />
                          {formik.touched.opsi_b && formik.errors.opsi_b ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.opsi_b}</div>
                          ) : null}
                          
                          {/* Input Nilai TKP untuk Opsi B - hanya tampil untuk TKP */}
                          {isCurrentSoalTKP && (
                            <div className="mt-2">
                              <Label htmlFor="nilai_opsi_b" className="form-label">Nilai TKP Opsi B <span className="text-danger">*</span></Label>
                              <Input
                                id="nilai_opsi_b"
                                name="nilai_opsi_b"
                                type="number"
                                placeholder="Masukkan nilai untuk opsi B"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.nilai_opsi_b || ""}
                                invalid={!!(formik.touched.nilai_opsi_b && formik.errors.nilai_opsi_b)}
                              />
                              <FormFeedback>{formik.errors.nilai_opsi_b}</FormFeedback>
                            </div>
                          )}
                          
                          <Input
                            id="image_opsi_b"
                            name="image_opsi_b"
                            type="file"
                            accept="image/*"
                            onChange={(event) => formik.setFieldValue("image_opsi_b", event.currentTarget.files[0] || null)}
                            className="form-control mt-2"
                          />
                          {renderImagePreview("image_opsi_b")}
                        </div>

                        {/* Opsi C */}
                        <div className="mb-3">
                          <Label htmlFor="opsi_c" className="form-label">Opsi C <span className="text-danger">*</span></Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.opsi_c || ''}
                            onChange={(content) => {
                              formik.setFieldValue("opsi_c", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("opsi_c", true);
                            }}
                            modules={modules}
                            formats={formats}
                            placeholder="Masukkan teks opsi C"
                            style={{ height: '100px', marginBottom: '80px' }}
                          />
                          {formik.touched.opsi_c && formik.errors.opsi_c ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.opsi_c}</div>
                          ) : null}
                          
                          {/* Input Nilai TKP untuk Opsi C - hanya tampil untuk TKP */}
                          {isCurrentSoalTKP && (
                            <div className="mt-2">
                              <Label htmlFor="nilai_opsi_c" className="form-label">Nilai TKP Opsi C <span className="text-danger">*</span></Label>
                              <Input
                                id="nilai_opsi_c"
                                name="nilai_opsi_c"
                                type="number"
                                placeholder="Masukkan nilai untuk opsi C"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.nilai_opsi_c || ""}
                                invalid={!!(formik.touched.nilai_opsi_c && formik.errors.nilai_opsi_c)}
                              />
                              <FormFeedback>{formik.errors.nilai_opsi_c}</FormFeedback>
                            </div>
                          )}
                          
                          <Input
                            id="image_opsi_c"
                            name="image_opsi_c"
                            type="file"
                            accept="image/*"
                            onChange={(event) => formik.setFieldValue("image_opsi_c", event.currentTarget.files[0] || null)}
                            className="form-control mt-2"
                          />
                          {renderImagePreview("image_opsi_c")}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* OPSI D - E */}
                  <Col md={6}>
                    <Card className="shadow-sm border h-100">
                      <CardBody>
                        <h5 className="font-size-16 mb-3 border-bottom pb-2">Opsi Jawaban</h5>
                        
                        {/* Opsi D */}
                        <div className="mb-3 border-bottom pb-3">
                          <Label htmlFor="opsi_d" className="form-label">Opsi D <span className="text-danger">*</span></Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.opsi_d || ''}
                            onChange={(content) => {
                              formik.setFieldValue("opsi_d", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("opsi_d", true);
                            }}
                            modules={modules}
                            formats={formats}
                            placeholder="Masukkan teks opsi D"
                            style={{ height: '100px', marginBottom: '80px' }}
                          />
                          {formik.touched.opsi_d && formik.errors.opsi_d ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.opsi_d}</div>
                          ) : null}
                          
                          {/* Input Nilai TKP untuk Opsi D - hanya tampil untuk TKP */}
                          {isCurrentSoalTKP && (
                            <div className="mt-2">
                              <Label htmlFor="nilai_opsi_d" className="form-label">Nilai TKP Opsi D <span className="text-danger">*</span></Label>
                              <Input
                                id="nilai_opsi_d"
                                name="nilai_opsi_d"
                                type="number"
                                placeholder="Masukkan nilai untuk opsi D"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.nilai_opsi_d || ""}
                                invalid={!!(formik.touched.nilai_opsi_d && formik.errors.nilai_opsi_d)}
                              />
                              <FormFeedback>{formik.errors.nilai_opsi_d}</FormFeedback>
                            </div>
                          )}
                          
                          <Input
                            id="image_opsi_d"
                            name="image_opsi_d"
                            type="file"
                            accept="image/*"
                            onChange={(event) => formik.setFieldValue("image_opsi_d", event.currentTarget.files[0] || null)}
                            className="form-control mt-2"
                          />
                          {renderImagePreview("image_opsi_d")}
                        </div>

                        {/* Opsi E */}
                        <div className="mb-3">
                          <Label htmlFor="opsi_e" className="form-label">Opsi E</Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.opsi_e || ''}
                            onChange={(content) => {
                              formik.setFieldValue("opsi_e", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("opsi_e", true);
                            }}
                            modules={modules}
                            formats={formats}
                            placeholder="Masukkan teks opsi E"
                            style={{ height: '100px', marginBottom: '80px' }}
                          />
                          {formik.touched.opsi_e && formik.errors.opsi_e ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.opsi_e}</div>
                          ) : null}
                          
                          {/* Input Nilai TKP untuk Opsi E - hanya tampil untuk TKP */}
                          {isCurrentSoalTKP && (
                            <div className="mt-2">
                              <Label htmlFor="nilai_opsi_e" className="form-label">Nilai TKP Opsi E</Label>
                              <Input
                                id="nilai_opsi_e"
                                name="nilai_opsi_e"
                                type="number"
                                placeholder="Masukkan nilai untuk opsi E"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.nilai_opsi_e || ""}
                                invalid={!!(formik.touched.nilai_opsi_e && formik.errors.nilai_opsi_e)}
                              />
                              <FormFeedback>{formik.errors.nilai_opsi_e}</FormFeedback>
                            </div>
                          )}
                          
                          <Input
                            id="image_opsi_e"
                            name="image_opsi_e"
                            type="file"
                            accept="image/*"
                            onChange={(event) => formik.setFieldValue("image_opsi_e", event.currentTarget.files[0] || null)}
                            className="form-control mt-2"
                          />
                          {renderImagePreview("image_opsi_e")}
                        </div>

                        {/* Informasi TKP */}
                        {isCurrentSoalTKP && (
                          <div className="alert alert-info mt-3">
                            <small>
                              <i className="mdi mdi-information-outline me-1"></i>
                              <strong>Informasi TKP:</strong> Soal TKP memerlukan nilai untuk setiap opsi jawaban. 
                              Nilai ini akan digunakan dalam perhitungan skor TKP.
                            </small>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <ModalFooter className="px-4 py-3 justify-content-end">
                  <Button color="light" onClick={toggleModal}>
                    Batal
                  </Button>
                  <Button color="primary" type="submit" disabled={formik.isSubmitting}>
                    {formik.isSubmitting ? "Menyimpan..." : (isEdit ? "Update" : "Simpan")}
                  </Button>
                </ModalFooter>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SoalList;